import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui';

/**
 * Fallback-Oberfläche, die statt einer weißen Seite erscheint, wenn ein
 * Render-Fehler bis zur ErrorBoundary durchschlägt. Eigene Funktionskomponente,
 * damit i18n-Hooks (`useTranslation`) verwendet werden können.
 */
function ErrorFallback({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-gradient-to-b from-slate-50 to-slate-200 px-6 py-20 text-center text-slate-900 dark:from-slate-900 dark:to-slate-950 dark:text-slate-100">
      <div className="text-5xl" aria-hidden="true">
        ⚠️
      </div>
      <div className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight">{t('errors.boundaryTitle')}</h1>
        <p className="mx-auto max-w-md text-sm text-slate-600 dark:text-slate-300">{t('errors.boundaryHint')}</p>
      </div>
      <Button onClick={onReset}>{t('errors.boundaryReload')}</Button>
    </div>
  );
}

type Props = { children: ReactNode };
type State = { hasError: boolean };

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // In Produktion landet das in der Browser-Konsole; ein Telemetrie-Hook kann hier andocken.
    console.error('Unerwarteter Fehler in der Anwendung:', error, info);
  }

  handleReset = () => {
    // Frischer Einstieg über die Startseite – verlässlicher als ein In-Place-Reset.
    window.location.assign('/');
  };

  override render() {
    if (this.state.hasError) {
      return <ErrorFallback onReset={this.handleReset} />;
    }
    return this.props.children;
  }
}
